import { useEffect, useReducer } from 'react';
import withErrorBoundary from 'src/lib/HOC/ErrorBoundary/withErrorBoundary';
import { useAppContext } from 'src/lib/Utils/Contexts/AppContext';
import Logger from 'src/lib/Utils/Logger';
import { AccountSelection } from './AccountSelection';
import { ErrorView } from './Error';
import { getAccounts } from './_api/accounts';
import { LoadingScreen } from './_components/LoadingScreen';
import { AccountSelectorContent } from './_content';
const resolver = (content: AccountSelectorContent) => {
  const selectAccount = content.fields.items.find(x => x.name === 'Select Account')!;

  return {
    returnCodes: content.fields['Return Codes'],
    guid: content.dataSource,
    maskAccountNumber: content.fields['Mask Account Number']?.value,
    maskAddress: content.fields['Mask Address']?.value,
    takingLongerText: content.fields['Taking Longer Text'].value,
    loadingText: content.fields['Loading Text'].value,
    items: content.fields.items,
    prepaidLabel: content.fields['Prepaid Label'].value,
    prepaidDashboardUrl: selectAccount.fields['Prepaid Dashboard URL'].value,
    dashboardUrl: selectAccount.fields['Dashboard URL'].value,
    loaderDuration: content.fields['Loader Duration'].value,
    getResiAccountsEndpoint: content.fields.Endpoint.fields.Phrase.value,
    selectAccountEndpoint: selectAccount.fields.Endpoint.fields.Phrase.value,
    selectAccount,
  };
};

export type ResolvedContent = ReturnType<typeof resolver>;

type Action = { type: 'ERROR'; payload: any } | { type: 'SUCCESS'; payload: any };
type State = { status: 'loading' | 'error' | 'success'; data: any; error: any };

const fetchReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ERROR': {
      return { ...state, status: 'error', error: action.payload };
    }
    case 'SUCCESS': {
      return { ...state, status: 'success', data: action.payload };
    }
    default: {
      throw new Error('Unsupported action');
    }
  }
};

const intitial = { status: 'loading' as const, data: undefined, error: undefined };

const InnerContent = (props: ResolvedContent) => {
  const [state, send] = useReducer(fetchReducer, intitial);
  const accountData = state.data;
  const hasLessThanSixAccounts = accountData && accountData.length < 6;

  useEffect(() => {
    getAccounts(props.getResiAccountsEndpoint)
      .then(d => {
        if (d?.Accounts) {
          send({ type: 'SUCCESS', payload: d?.Accounts });
        } else {
          send({ type: 'ERROR', payload: d });
        }
      })
      .catch(e => {
        send({ type: 'ERROR', payload: e });
        Logger(e, { componentName: 'AccountSelector', message: 'getResiAccounts error' });
      });
  }, []);

  const navigateToAccount = (accountUrl: string) => {
    // TODO check for internal url and route using react-router push ONCE Dashboard is built
    window.location.href = accountUrl;
  };

  const checkUserRouting = () => {
    const firstLink = props.dashboardUrl.href;

    if (hasLessThanSixAccounts) {
      navigateToAccount(firstLink);
    }
  };

  // If there are less than 6 accounts, we skip rendering any and redirect the user to the first link
  const loaderIsVisible = state.status === 'loading' || hasLessThanSixAccounts;
  const errorIsVisible = state.status === 'error';

  if (loaderIsVisible) {
    return (
      <LoadingScreen
        onDone={() => checkUserRouting()}
        status={state.status}
        doneAfter={0}
        messages={[
          {
            message: props.loadingText,
            meta: {
              to: 100,
              duration: props.loaderDuration,
            },
          },
          {
            message: props.takingLongerText,
            meta: {
              to: 40,
              duration: props.loaderDuration,
            },
          },
        ]}
      />
    );
  }

  if (errorIsVisible) {
    const returnCode = state.error?.ReturnCode || 'E';
    const errorMessage = props.returnCodes.find(x => x.name === `${returnCode}`);

    return (
      <ErrorView
        error={errorMessage?.fields}
        returnCode={returnCode}
        endpoint={props.getResiAccountsEndpoint}
      />
    );
  }

  return (
    <AccountSelection
      prepaidLabel={props.prepaidLabel}
      accountMasking={props.maskAccountNumber}
      addressMasking={props.maskAddress}
      accounts={accountData}
      selectedAccount={props.selectAccount}
      selectAccountEndpoint={props.selectAccountEndpoint}
      returnCodes={props.returnCodes}
    />
  );
};

const Main = (props: ResolvedContent) => {
  return (
    <section className="flex flex-col flex-grow gap-32 px-0 sm:px-24 pb-36 md:pb-48 pt-18 md:pt-36 md:gap-48">
      <section className="w-full container-lg">
        <InnerContent {...props} />
      </section>
    </section>
  );
};

const AccountSelector = (props: any) => {
  const { status } = useAppContext();
  const isNotAuthenticated = !status.isAuthenticated;

  const redirect = props.fields?.['Unauthenticated Redirect']?.value?.href || '/my-account/sign-in';

  const content = resolver(props);

  useEffect(() => {
    if (isNotAuthenticated) {
      window.location.href = redirect;
    }
  }, [isNotAuthenticated, redirect]);

  return <Main {...content} />;
};

export default withErrorBoundary(AccountSelector);
